import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import axios from 'axios';
import { buildApiUrl } from '../../config/api';

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        destinations: 0,
        packages: 0,
        bookings: 0,
        campingBookings: 0,
        messages: 0,
        revenue: 0,
        campingRevenue: 0,
        subscribers: 0
    });
    const [recentBookings, setRecentBookings] = useState([]);
    const [recentCampingBookings, setRecentCampingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('❌ No token found');
            return null;
        }
        return token;
    };

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = getToken();
            if (!token) {
                window.location.href = '/admin/login';
                return;
            }

            const headers = { 
                'Authorization': `Bearer ${token}`,
                'x-auth-token': token,
                'Content-Type': 'application/json'
            };

            // Fetch all data in parallel with error handling
            const [destinationsRes, packagesRes, bookingsRes, campingBookingsRes, messagesRes, newsletterStatsRes] = await Promise.allSettled([
                axios.get(buildApiUrl('/destinations'), { headers }),
                axios.get(buildApiUrl('/packages'), { headers }),
                axios.get(buildApiUrl('/bookings'), { headers }),
                axios.get(buildApiUrl('/camping/admin/bookings'), { headers }),
                axios.get(buildApiUrl('/contact/messages'), { headers }).catch(() => ({ data: [] })),
                axios.get(buildApiUrl('/contact/newsletter/stats'), { headers }).catch(() => ({ data: { data: { active: 0 } } }))
            ]);

            // Process destinations
            const destinations = destinationsRes.status === 'fulfilled' ? (destinationsRes.value.data?.data || destinationsRes.value.data || []) : [];
            const destinationsCount = Array.isArray(destinations) ? destinations.length : 0;

            // Process packages
            const packages = packagesRes.status === 'fulfilled' ? (packagesRes.value.data?.data || packagesRes.value.data || []) : [];
            const packagesCount = Array.isArray(packages) ? packages.length : 0;

            // Process bookings
            let bookingsData = [];
            let totalRevenue = 0;
            if (bookingsRes.status === 'fulfilled') {
                bookingsData = bookingsRes.value.data?.data || bookingsRes.value.data || [];
                if (Array.isArray(bookingsData)) {
                    totalRevenue = bookingsData.reduce((sum, booking) => sum + (parseFloat(booking.total_amount) || 0), 0);
                }
            }
            const bookingsCount = Array.isArray(bookingsData) ? bookingsData.length : 0;

            // Process camping bookings
            let campingBookingsData = [];
            let campingRevenue = 0;
            if (campingBookingsRes.status === 'fulfilled') {
                const campingPayload = campingBookingsRes.value.data?.data || campingBookingsRes.value.data || [];
                campingBookingsData = Array.isArray(campingPayload)
                    ? campingPayload
                    : (campingPayload.bookings || []);
                if (Array.isArray(campingBookingsData)) {
                    campingRevenue = campingBookingsData.reduce((sum, booking) => sum + (parseFloat(booking.total_amount) || 0), 0);
                }
            }
            const campingBookingsCount = Array.isArray(campingBookingsData) ? campingBookingsData.length : 0;

            // Process messages
            const messages = messagesRes.status === 'fulfilled' ? (messagesRes.value.data?.data || messagesRes.value.data || []) : [];
            const messagesCount = Array.isArray(messages) ? messages.length : 0;
            const newsletterStats = newsletterStatsRes.status === 'fulfilled'
                ? (newsletterStatsRes.value.data?.data || newsletterStatsRes.value.data || {})
                : {};

            // Get recent bookings (last 5)
            const recent = [...(Array.isArray(bookingsData) ? bookingsData : [])]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5);

            // Get recent camping bookings
            const recentCamping = [...(Array.isArray(campingBookingsData) ? campingBookingsData : [])]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5);

            setStats({
                destinations: destinationsCount,
                packages: packagesCount,
                bookings: bookingsCount,
                campingBookings: campingBookingsCount,
                messages: messagesCount,
                revenue: totalRevenue,
                campingRevenue: campingRevenue,
                subscribers: Number(newsletterStats.active || 0)
            });

            setRecentBookings(recent);
            setRecentCampingBookings(recentCamping);

        } catch (error) {
            console.error('❌ Error fetching dashboard data:', error);
            setError('Failed to load dashboard data. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusBadgeClass = (status) => {
        const classes = {
            'confirmed': 'status-confirmed',
            'pending': 'status-pending',
            'completed': 'status-completed',
            'cancelled': 'status-cancelled'
        };
        return classes[status] || 'status-default';
    };

    const statCards = [
        { 
            title: 'Destinations', 
            value: stats.destinations, 
            icon: 'fas fa-map-marked-alt', 
            color: '#4299e1', 
            link: '/admin/destinations',
            bg: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)'
        },
        { 
            title: 'Packages', 
            value: stats.packages, 
            icon: 'fas fa-box', 
            color: '#48bb78', 
            link: '/admin/packages',
            bg: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
        },
        { 
            title: 'Package Bookings', 
            value: stats.bookings, 
            icon: 'fas fa-calendar-check', 
            color: '#ed8936', 
            link: '/admin/bookings',
            bg: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)'
        },
        { 
            title: 'Camping Bookings', 
            value: stats.campingBookings, 
            icon: 'fas fa-campground', 
            color: '#9f7aea', 
            link: '/admin/camping/bookings',
            bg: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)'
        },
        { 
            title: 'Package Revenue', 
            value: formatCurrency(stats.revenue), 
            icon: 'fas fa-rupee-sign', 
            color: '#f56565', 
            link: '/admin/bookings',
            bg: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)'
        },
        { 
            title: 'Camping Revenue', 
            value: formatCurrency(stats.campingRevenue), 
            icon: 'fas fa-tree', 
            color: '#38b2ac', 
            link: '/admin/camping/bookings',
            bg: 'linear-gradient(135deg, #38b2ac 0%, #319795 100%)'
        },
        { 
            title: 'Contact Messages', 
            value: stats.messages, 
            icon: 'fas fa-envelope', 
            color: '#ed64a6', 
            link: '/admin/messages',
            bg: 'linear-gradient(135deg, #ed64a6 0%, #d53f8c 100%)'
        },
        { 
            title: 'Newsletter Subs', 
            value: stats.subscribers, 
            icon: 'fas fa-newspaper', 
            color: '#a0aec0', 
            link: '/admin/newsletter',
            bg: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)'
        }
    ];

    if (loading) {
        return (
            <div className="admin-layout">
                <AdminSidebar />
                <div className="admin-main">
                    <AdminHeader title="Dashboard" />
                    <div className="dashboard-loading">
                        <div className="spinner"></div>
                        <p>Loading dashboard data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-layout">
                <AdminSidebar />
                <div className="admin-main">
                    <AdminHeader title="Dashboard" />
                    <div className="dashboard-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>{error}</p>
                        <button onClick={fetchDashboardData} className="retry-btn">
                            <i className="fas fa-sync-alt"></i> Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader 
                    title="Dashboard" 
                    subtitle="Welcome back! Here's what's happening with your business."
                />
                
                <div className="dashboard-content">
                    {/* Stats Grid */}
                    <div className="stats-grid">
                        {statCards.map((stat, index) => (
                            <Link to={stat.link} key={index} className="stat-card">
                                <div className="stat-icon" style={{ background: stat.bg }}>
                                    <i className={stat.icon}></i>
                                </div>
                                <div className="stat-info">
                                    <h3>{stat.value}</h3>
                                    <p>{stat.title}</p>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Recent Package Bookings */}
                    <div className="recent-section">
                        <div className="section-header">
                            <h2>
                                <i className="fas fa-calendar-check"></i>
                                Recent Package Bookings
                            </h2>
                            <Link to="/admin/bookings" className="view-all-link">
                                View All <i className="fas fa-arrow-right"></i>
                            </Link>
                        </div>

                        <div className="table-wrapper">
                            {recentBookings.length > 0 ? (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Booking ID</th>
                                            <th>Customer</th>
                                            <th>Package</th>
                                            <th>Travel Date</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentBookings.map(booking => (
                                                <tr key={booking.id}>
                                                    <td>
                                                        <span className="booking-id">{booking.booking_id}</span>
                                                    </td>
                                                    <td>
                                                        <div className="customer-cell">
                                                            <div className="customer-avatar">
                                                                {booking.customer_name?.charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="customer-name">{booking.customer_name}</div>
                                                                <div className="customer-email">{booking.customer_email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="package-name">{booking.package_title || 'N/A'}</span>
                                                    </td>
                                                    <td>
                                                        <div className="date-cell">
                                                            <i className="far fa-calendar-alt"></i>
                                                            {formatDate(booking.travel_date)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="amount">{formatCurrency(booking.total_amount)}</span>
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${getStatusBadgeClass(booking.booking_status)}`}>
                                                            {booking.booking_status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Link to={`/admin/bookings/${booking.id}`} className="action-icon" title="View Details">
                                                            <i className="fas fa-eye"></i>
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="empty-state">
                                        <i className="fas fa-inbox"></i>
                                        <p>No recent bookings found</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Recent Camping Bookings */}
                        <div className="recent-section">
                            <div className="section-header">
                                <h2>
                                    <i className="fas fa-campground"></i>
                                    Recent Camping Bookings
                                </h2>
                                <Link to="/admin/camping/bookings" className="view-all-link">
                                    View All <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>

                            <div className="table-wrapper">
                                {recentCampingBookings.length > 0 ? (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Booking ID</th>
                                                <th>Customer</th>
                                                <th>Site</th>
                                                <th>Check In/Out</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentCampingBookings.map(booking => (
                                                    <tr key={booking.id}>
                                                        <td>
                                                            <span className="booking-id">{booking.booking_id}</span>
                                                        </td>
                                                        <td>
                                                            <div className="customer-cell">
                                                                <div className="customer-avatar">
                                                                    {booking.customer_name?.charAt(0).toUpperCase()}
                                                                </div>
                                                                <div>
                                                                    <div className="customer-name">{booking.customer_name}</div>
                                                                    <div className="customer-email">{booking.customer_email}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="package-name">{booking.site_name || 'N/A'}</span>
                                                        </td>
                                                        <td>
                                                            <div className="date-cell">
                                                                <i className="fas fa-calendar-plus"></i>
                                                                {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="amount">{formatCurrency(booking.total_amount)}</span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${getStatusBadgeClass(booking.booking_status)}`}>
                                                                {booking.booking_status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Link to={`/admin/camping/bookings/${booking.id}`} className="action-icon" title="View Details">
                                                                <i className="fas fa-eye"></i>
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="empty-state">
                                            <i className="fas fa-inbox"></i>
                                            <p>No recent camping bookings found</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="quick-actions">
                              
                                <div className="actions-grid">
                                    <Link to="/admin/destinations/new" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-plus-circle"></i>
                                        </div>
                                        <span>Add Destination</span>
                                    </Link>
                                    <Link to="/admin/packages/new" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-box"></i>
                                        </div>
                                        <span>Add Package</span>
                                    </Link>
                                    <Link to="/admin/camping/sites/new" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-campground"></i>
                                        </div>
                                        <span>Add Camping Site</span>
                                    </Link>
                                    <Link to="/admin/messages" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-envelope"></i>
                                        </div>
                                        <span>View Messages</span>
                                    </Link>
                                    <Link to="/admin/bookings" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-calendar-check"></i>
                                        </div>
                                        <span>Manage Bookings</span>
                                    </Link>
                                    <Link to="/admin/camping/bookings" className="action-card">
                                        <div className="action-icon">
                                            <i className="fas fa-tree"></i>
                                        </div>
                                        <span>Camping Bookings</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
<style>{`
   /* AdminDashboard.css */

.admin-layout {
    display: flex;
    min-height: 100vh;
    background: #f8fafc;
}

.admin-main {
    flex: 1;
    margin-left: 280px;
    background: #f8fafc;
    padding: 2rem;
}

/* Loading State */
.dashboard-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 20px;
    margin-top: 1rem;
    padding: 3rem;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #e2e8f0;
    border-top-color: #2e7d32;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Error State */
.dashboard-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 20px;
    margin-top: 1rem;
    padding: 3rem;
    text-align: center;
}

.dashboard-error i {
    font-size: 3rem;
    color: #ef4444;
    margin-bottom: 1rem;
}

.dashboard-error p {
    color: #64748b;
    margin-bottom: 1.5rem;
}

.retry-btn {
    padding: 0.6rem 1.5rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 30px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.retry-btn:hover {
    background: #1b5e20;
    transform: translateY(-2px);
}

/* Dashboard Content */
.dashboard-content {
    margin-top: 1rem;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.stat-card {
    background: white;
    border-radius: 20px;
    padding: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    text-decoration: none;
    transition: all 0.3s;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -8px rgba(0,0,0,0.15);
}

.stat-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
}

.stat-info h3 {
    font-size: 1.8rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 0.25rem;
}

.stat-info p {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0;
}

/* Recent Section */
.recent-section {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    margin-bottom: 2rem;
    overflow: hidden;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.section-header h2 {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.section-header h2 i {
    color: #2e7d32;
}

.view-all-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #2e7d32;
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.2s;
}

.view-all-link:hover {
    gap: 0.75rem;
    color: #1b5e20;
}

/* Table Styles */
.table-wrapper {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table th {
    text-align: left;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #e2e8f0;
}

.data-table td {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.data-table tr:hover td {
    background: #f8fafc;
}

/* Booking ID */
.booking-id {
    font-family: monospace;
    font-size: 0.8rem;
    font-weight: 600;
    color: #2e7d32;
    background: #e8f5e9;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
    display: inline-block;
}

/* Customer Cell */
.customer-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.customer-avatar {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #2e7d32, #1b5e20);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1rem;
}

.customer-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.85rem;
}

.customer-email {
    font-size: 0.7rem;
    color: #64748b;
}

/* Package Name */
.package-name {
    font-weight: 500;
    color: #1e293b;
    font-size: 0.85rem;
}

/* Date Cell */
.date-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: #475569;
}

.date-cell i {
    color: #2e7d32;
    font-size: 0.7rem;
}

/* Amount */
.amount {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
}

/* Status Badge */
.status-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
}

.status-confirmed {
    background: #dcfce7;
    color: #166534;
}

.status-pending {
    background: #fef9c3;
    color: #854d0e;
}

.status-completed {
    background: #dbeafe;
    color: #1e40af;
}

.status-cancelled {
    background: #fee2e2;
    color: #991b1b;
}

.status-default {
    background: #f1f5f9;
    color: #475569;
}

/* Action Icon */
.action-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #f1f5f9;
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    transition: all 0.2s;
}

.action-icon:hover {
    background: #e8f5e9;
    color: #2e7d32;
    transform: translateY(-2px);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 3rem;
    color: #94a3b8;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
}

.empty-state p {
    margin: 0;
}

/* Quick Actions */
.quick-actions {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    margin-bottom: 2rem;
}

.actions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 1.5rem;
}

.action-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: #f8fafc;
    border-radius: 12px;
    text-decoration: none;
    transition: all 0.2s;
    border: 1px solid #e2e8f0;
}

.action-card:hover {
    background: #e8f5e9;
    border-color: #2e7d32;
    transform: translateX(4px);
}

.action-card .action-icon {
    width: 40px;
    height: 40px;
    background: white;
    color: #2e7d32;
    font-size: 1.2rem;
    margin: 0;
}

.action-card span {
    font-size: 0.9rem;
    font-weight: 500;
    color: #1e293b;
}

.action-card:hover span {
    color: #2e7d32;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
        padding: 1rem;
    }
    
    .stats-grid {
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }
    
    .actions-grid {
        grid-template-columns: 1fr;
    }
    
    .data-table th,
    .data-table td {
        padding: 0.75rem 1rem;
    }
    
    .customer-cell {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .customer-avatar {
        width: 32px;
        height: 32px;
        font-size: 0.8rem;
    }
`}      
    </style> 
                </div>
            );
        };
        
        export default AdminDashboard;
